import { Component, ComponentRef, Input, OnDestroy, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, combineLatest, Observable, Subject, Subscription } from 'rxjs';
import { map, startWith, take } from 'rxjs/operators';
import { orderBy } from 'lodash';

import { TranslateService } from '../../../../translate/translate.service';
import {
  IDclConfig,
  ITableConfig,
  ITableRow,
  SortEvent,
} from '../../../../components/table/ngb-table/utilities/ngb-table.interface';
import { DEFAULT_ENTRY_LIMITS } from '../../../../constants/form.constants';
import { NgbTableUtilities } from '../../../../components/table/ngb-table/utilities/ngb-table.utilities';
import { ITenantUser } from '../../../../../../shared/interfaces/tenant.interface';
import { SharedCommonUtility } from '../../../../../../shared/utils/common.utility';
import { $user, accountStatus } from '../../../../../../shared/constants/user';
import { $tenantUser, $tenantUserIntegrations } from '../../../../../../shared/constants/tenant';
import { $sortingOrder } from '../../../../../../shared/constants/sort';
import { TenantService } from '../../../../services/tenant.service';
import { NotificationService } from '../../../../services/notification.service';
import { NotificationPosition } from '../../../../models/notification.model';
import { LoadErrorHandler } from '../../../../services/load-error-handler';
import { ModalService } from '../../../../services/modal.service';
import { AssignAcademyRolesModalComponent } from '../assign-academy-roles/assign-academy-roles-modal.component';
import { AcademyUserRole } from '../../../../../../shared/constants/academy-user';
import { UserAclService } from '../../../../services/user-acl.service';
import { AclSecurityAdapter } from '../../../../services/acl.service';
import { RequiredSecurities } from '../../../../../../shared/constants/required-securities';
import { IDropdownItem } from '../../../../components/table/ngb-table/cells/dropdown-cell/dropdown-cell.component';
import { IOrderedListCellConfig } from '../../../../components/table/ngb-table/cells/ordered-list-cell/ordered-list-cell.component';
import { ITextCellConfig } from '../../../../components/table/ngb-table/cells/text-cell/text-cell.component';
import { UserStatusUtility } from '../../../../utility/user-status.utility';

enum TableColumns {
  firstName = 'user.firstName',
  lastName = 'user.lastName',
  email = 'user.email',
  roles = 'integrations.academy.roles',
  academySeat = 'integrations.academy.enabled',
}

@Component({
  selector: 'app-tenant-academy-table',
  templateUrl: './tenant-academy-table.component.html',
  standalone: false,
})
export class TenantAcademyTableComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription;
  public tableData$: Observable<ITableRow[]>;

  public usersCount$: Observable<number>;
  public page$: BehaviorSubject<number>;
  public pageSizeLimits: readonly number[];
  public onSort$: BehaviorSubject<SortEvent>;
  public slicedTenantUsers$: Observable<ITenantUser[]>;

  @Input()
  public tenantUsers$: Observable<ITenantUser[]>;
  @Input()
  public requestUsersReload$: Subject<unknown>;

  public get page(): number {
    return this.page$.value;
  }
  public set page(value: number) {
    this.page$.next(value);
  }

  public pageSize$: BehaviorSubject<number>;
  public get pageSize(): number {
    return this.pageSize$.value;
  }
  public set pageSize(value: number) {
    this.pageSize$.next(value);
  }

  constructor(
    private translateService: TranslateService,
    private userAclService: UserAclService,
    private tenantService: TenantService,
    private notificationService: NotificationService,
    private modalService: ModalService,
    private loadError: LoadErrorHandler,
  ) {
    this.subscriptions = new Subscription();
    this.pageSizeLimits = DEFAULT_ENTRY_LIMITS;
    this.page$ = new BehaviorSubject<number>(1);
    this.pageSize$ = new BehaviorSubject<number>(DEFAULT_ENTRY_LIMITS[DEFAULT_ENTRY_LIMITS.length - 1]);
    this.onSort$ = new BehaviorSubject<SortEvent>(null);
  }

  private toTableData(entries: ITenantUser[], hasCreatePermission: boolean, hasUpdatePermission: boolean): ITableRow[] {
    return entries.map((entry: ITenantUser) => {
      const dropdownItems: IDropdownItem[] = [];

      const integrationEnabled: boolean = entry[$tenantUser.integrations]?.[$tenantUserIntegrations.academy]?.enabled ?? false;
      if (!integrationEnabled) {
        if (hasCreatePermission) {
          dropdownItems.push({
            label: this.translateService.instant(`activate_user`),
            clickListener: (): void => this.activateUser(entry),
          });
        }
      } else {
        dropdownItems.push({
          label: this.translateService.instant(`deactivate_user`),
          clickListener: (): void => this.deactivateUser(entry),
        });

        if (hasUpdatePermission) {
          dropdownItems.push({
            label: this.translateService.instant(`change_role`),
            clickListener: (): void => this.assignRoles(entry),
          });
        }
      }

      if (SharedCommonUtility.isNullishOrEmpty(dropdownItems)) {
        dropdownItems.push({
          disabled: true,
          label: this.translateService.instant('no_actions_available'),
          clickListener: () => {},
        });
      }

      const academySeatStatus: accountStatus = integrationEnabled ? accountStatus.active : accountStatus.inactive;

      return {
        data: {
          [TableColumns.firstName]: NgbTableUtilities.textCell({ text: entry[$tenantUser.user][$user.firstName] }),
          [TableColumns.lastName]: NgbTableUtilities.textCell({ text: entry[$tenantUser.user][$user.lastName] }),
          [TableColumns.email]: NgbTableUtilities.textCell({ text: entry[$tenantUser.user][$user.email] }),
          [TableColumns.roles]: this.getRolesCell(entry),
          [TableColumns.academySeat]: NgbTableUtilities.DSChipCell({
            text: this.translateService.instant(UserStatusUtility.getStatusTranslationKey(academySeatStatus)),
            chipColorVariant: UserStatusUtility.getStatusChipVariant(academySeatStatus),
          }),
          actions: NgbTableUtilities.dropdownCell({
            ellipsisIcon: true,
            classes: ['btn-link', 'px-0'],
            dropdownItems: dropdownItems,
          }),
        },
      };
    });
  }

  private getRolesCell(entry: ITenantUser): IDclConfig<ITextCellConfig | IOrderedListCellConfig> {
    let roles: AcademyUserRole | AcademyUserRole[] =
      entry[$tenantUser.integrations]?.[$tenantUserIntegrations.academy]?.roles ?? [];

    if (!Array.isArray(roles)) {
      roles = [roles];
    }

    if (roles.length === 1) {
      return NgbTableUtilities.textCell({ text: roles[0] ? this.translateService.instant(roles[0]) : '' });
    }

    return NgbTableUtilities.orderedListCell({
      items: roles.map((role: AcademyUserRole) => {
        return {
          text: this.translateService.instant(role),
        };
      }),
    });
  }

  private loadTable(): void {
    const hasCreatePermissions$: Observable<boolean> = this.userAclService
      .createCheckAccessForCurrentUser()
      .pipe(
        map((adapter: AclSecurityAdapter): boolean =>
          adapter.useEntityLevel().useFunctionalActions(RequiredSecurities.Admin_Academy_Create.functionalActions).check(),
        ),
      );

    const hasUpdatePermissions$: Observable<boolean> = this.userAclService
      .createCheckAccessForCurrentUser()
      .pipe(
        map((adapter: AclSecurityAdapter): boolean =>
          adapter.useEntityLevel().useFunctionalActions(RequiredSecurities.Admin_Academy_Update.functionalActions).check(),
        ),
      );

    this.tableData$ = combineLatest([this.slicedTenantUsers$, hasCreatePermissions$, hasUpdatePermissions$]).pipe(
      map(([tenantUsers, hasCreatePermissions, hasUpdatePermissions]: [ITenantUser[], boolean, boolean]): ITableRow[] => {
        return this.toTableData(tenantUsers, hasCreatePermissions, hasUpdatePermissions);
      }),
    );
  }

  private activateUser(user: ITenantUser): void {
    const onActivateUserSuccess = (): void => {
      this.requestUsersReload$.next();
      this.notificationService.success(this.translateService.instant('academy_seat_activated'), NotificationPosition.Toast);
    };

    const onActivateUserError = (error: HttpErrorResponse): void => {
      this.loadError.handleError('user_activation_error', error, 'TenantAcademyTableComponent', true);
    };

    this.subscriptions.add(
      this.tenantService.activateUserInAcademy(user[$tenantUser.tenantId], user[$tenantUser.userId]).subscribe({
        next: onActivateUserSuccess.bind(this),
        error: onActivateUserError.bind(this),
      }),
    );
  }

  private deactivateUser(user: ITenantUser): void {
    const onDeactivateUserSuccess = (): void => {
      this.requestUsersReload$.next();
      this.notificationService.success(this.translateService.instant('academy_seat_deactivated'), NotificationPosition.Toast);
    };

    const onDeactivateUserError = (error: HttpErrorResponse): void => {
      this.loadError.handleError('user_deactivation_error', error, 'TenantAcademyTableComponent', true);
    };

    this.subscriptions.add(
      this.tenantService.deactivateUserInAcademy(user[$tenantUser.tenantId], user[$tenantUser.userId]).subscribe({
        next: onDeactivateUserSuccess.bind(this),
        error: onDeactivateUserError.bind(this),
      }),
    );
  }

  private assignRoles(user: ITenantUser): void {
    const ref: ComponentRef<AssignAcademyRolesModalComponent> = this.modalService.open(AssignAcademyRolesModalComponent);

    ref.instance.init(user);

    this.subscriptions.add(
      ref.instance.container.onClose$.pipe(take(1)).subscribe({
        next: () => {
          if (ref.instance.submitted) {
            this.requestUsersReload$.next();
          }
        },
      }),
    );
  }

  public get tableConfig(): ITableConfig {
    return {
      columns: {
        [TableColumns.firstName]: {
          translationKey: 'first_name',
          sortingEnabled: true,
        },
        [TableColumns.lastName]: {
          translationKey: 'last_name',
          sortingEnabled: true,
        },
        [TableColumns.email]: {
          translationKey: 'email',
          sortingEnabled: true,
        },
        [TableColumns.roles]: {
          translationKey: 'roles',
          sortingEnabled: true,
        },
        [TableColumns.academySeat]: {
          translationKey: 'academy_seat',
          sortingEnabled: true,
        },
        actions: {
          translationKey: 'table_column_actions',
          styles: {
            width: '5rem',
          },
        },
      },
      caption: this.translateService.instant('academy'),
      emptyState: {
        title: this.translateService.instant('no_users_for_tenant'),
        subtitle: this.translateService.instant('no_users_for_tenant_hint'),
      },
    };
  }

  public ngOnInit(): void {
    this.slicedTenantUsers$ = combineLatest([this.tenantUsers$, this.page$, this.pageSize$, this.onSort$]).pipe(
      map(([tenantUsers, page, pageSize, onSort]: [ITenantUser[], number, number, SortEvent]) => {
        let sortedUsers: ITenantUser[] = [...tenantUsers];

        if (!SharedCommonUtility.isNullish(onSort) && onSort.direction !== $sortingOrder.all) {
          sortedUsers = orderBy(sortedUsers, onSort.column, onSort.direction) as ITenantUser[];
        }

        return sortedUsers.slice((page - 1) * pageSize, page * pageSize);
      }),
    );
    this.loadTable();
    this.usersCount$ = this.tenantUsers$.pipe(
      map((tenantUsers: ITenantUser[]) => tenantUsers.length),
      startWith(0),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
